// src/services/api/dataApiServices.ts
import { authTokenManager } from '../../utils/authTokenManager';

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  statusCode?: number;
}

/**
 * Base API service with common functionality
 */
class BaseApiService {
  protected baseUrl: string;
  
  constructor(endpoint: string) {
    this.baseUrl = `${import.meta.env.VITE_API_BASE_URL || ''}/api/user-data/${endpoint}`;
    //console.log(`[BaseApiService] Initialized with base URL: ${this.baseUrl}`);
  }
  
  /**
   * Get authentication headers for API requests
   */
  protected async getHeaders(): Promise<HeadersInit> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json'
    };
    
    // Get the current auth token
    const token = await authTokenManager.getValidToken();
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    return headers;
  }
  
  /**
   * Handle API errors consistently
   */
  protected handleError(error: any): ApiResponse<any> {
    console.error(`[${this.constructor.name}] API error:`, error);
    
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

/**
 * API service for conversation data
 */
class ConversationApiService extends BaseApiService {
  constructor() {
    super('conversation');
  }
  
  /**
   * Save conversation data
   */
  public async saveConversation(conversationData: any): Promise<ApiResponse<any>> {
    try {
      const headers = await this.getHeaders();
      
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(conversationData)
      });
      
      if (!response.ok) {
        return {
          success: false,
          error: `Server responded with ${response.status}: ${response.statusText}`,
          statusCode: response.status
        };
      }
      
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      return this.handleError(error);
    }
  }
  

  public async saveConversationElements(elements: any[]): Promise<ApiResponse<any>> {
    try {
      const headers = await this.getHeaders();
      
      console.log(`[ConversationApiService] Delta sync: sending ${elements.length} changed elements`);
      
      const response = await fetch(`${this.baseUrl}/delta`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ elements })
      });
      
      if (!response.ok) {
        return {
          success: false,
          error: `Server responded with ${response.status}: ${response.statusText}`,
          statusCode: response.status
        };
      }
      
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      return this.handleError(error);
    }
  }


  /**
   * Get conversation data
   */
  public async getConversation(): Promise<ApiResponse<any>> {
    try {
      const headers = await this.getHeaders();
      
      const response = await fetch(this.baseUrl, {
        method: 'GET',
        headers
      });
      
      if (!response.ok) {
        return {
          success: false,
          error: `Server responded with ${response.status}: ${response.statusText}`,
          statusCode: response.status
        };
      }
      
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      return this.handleError(error);
    }
  }
}

/**
 * API service for location data
 */
class LocationApiService extends BaseApiService {
  constructor() {
    super('locations');
  }
  
  /**
   * Save location data
   */
  public async saveLocations(locations: any[]): Promise<ApiResponse<any>> {
    try {
      const headers = await this.getHeaders();
      
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify({ locations })
      });
      
      if (!response.ok) {
        return {
          success: false,
          error: `Server responded with ${response.status}: ${response.statusText}`,
          statusCode: response.status
        };
      }
      
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      return this.handleError(error);
    }
  }

  // In dataApiServices.ts
  public async saveLocationsDelta(locations: any[]): Promise<ApiResponse<any>> {
    try {
      const headers = await this.getHeaders();
      
      const response = await fetch(`${this.baseUrl}/delta`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ locations })
      });
      
      if (!response.ok) {
        return {
          success: false,
          error: `Server responded with ${response.status}: ${response.statusText}`,
          statusCode: response.status
        };
      }
      
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      return this.handleError(error);
    }
  }
  
  /**
   * Get location data
   */
  public async getLocations(): Promise<ApiResponse<any>> {
    try {
      const headers = await this.getHeaders();
      
      const response = await fetch(this.baseUrl, {
        method: 'GET',
        headers
      });
      
      if (!response.ok) {
        return {
          success: false,
          error: `Server responded with ${response.status}: ${response.statusText}`,
          statusCode: response.status
        };
      }
      
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      return this.handleError(error);
    }
  }
}

/**
 * API service for weather data
 */
class WeatherApiService extends BaseApiService {
  constructor() {
    super('weather');
  }
  
  /**
   * Save weather data
   */
  public async saveWeatherData(weatherData: any[]): Promise<ApiResponse<any>> {
    try {
      const headers = await this.getHeaders();
      
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify({ weatherData })
      });
      
      if (!response.ok) {
        return {
          success: false,
          error: `Server responded with ${response.status}: ${response.statusText}`,
          statusCode: response.status
        };
      }
      
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Add to WeatherApiService class
  public async saveWeatherDelta(widgets: any[]): Promise<ApiResponse<any>> {
    try {
      const headers = await this.getHeaders();
      const response = await fetch(`${this.baseUrl}/delta`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ widgets })
      });
      
      if (!response.ok) {
        return {
          success: false,
          error: `Server responded with ${response.status}: ${response.statusText}`,
          statusCode: response.status
        };
      }
      
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      return this.handleError(error);
    }
  }
  
  /**
   * Get weather data
   */
  public async getWeatherData(): Promise<ApiResponse<any>> {
    try {
      const headers = await this.getHeaders();
      
      const response = await fetch(this.baseUrl, {
        method: 'GET',
        headers
      });
      
      if (!response.ok) {
        return {
          success: false,
          error: `Server responded with ${response.status}: ${response.statusText}`,
          statusCode: response.status
        };
      }
      
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      return this.handleError(error);
    }
  }
}

/**
 * API service for image data
 */
class ImageApiService extends BaseApiService {
  constructor() {
    super('images');
  }
  
  /**
   * Save image data
   */
  public async saveImages(images: any[]): Promise<ApiResponse<any>> {
    
    console.log('[ImageApiService] Saving images:', images.length);
    console.log('[ImageApiService] Request URL:', this.baseUrl);
  
    try {
      const headers = await this.getHeaders();
      console.log('[ImageApiService] Headers:', headers);

      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify({ images })
      });
      
      if (!response.ok) {
        return {
          success: false,
          error: `Server responded with ${response.status}: ${response.statusText}`,
          statusCode: response.status
        };
      }
      
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      return this.handleError(error);
    }
  }
  
  // Add to ImageApiService class
  public async saveImagesDelta(images: any[]): Promise<ApiResponse<any>> {
    try {
      const headers = await this.getHeaders();
      const response = await fetch(`${this.baseUrl}/delta`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ images })
      });
      
      if (!response.ok) {
        return {
          success: false,
          error: `Server responded with ${response.status}: ${response.statusText}`,
          statusCode: response.status
        };
      }
      
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * Get image data
   */
  public async getImages(): Promise<ApiResponse<any>> {
    try {
      const headers = await this.getHeaders();
      
      const response = await fetch(this.baseUrl, {
        method: 'GET',
        headers
      });
      
      if (!response.ok) {
        return {
          success: false,
          error: `Server responded with ${response.status}: ${response.statusText}`,
          statusCode: response.status
        };
      }
      
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      return this.handleError(error);
    }
  }
}

/**
 * API service for map state
 */
class MapStateApiService extends BaseApiService {
  constructor() {
    super('mapstate');
  }
  
  /**
   * Save map state
   */
  public async saveMapState(mapState: any): Promise<ApiResponse<any>> {
    try {
      const headers = await this.getHeaders();
      
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(mapState)
      });
      
      if (!response.ok) {
        return {
          success: false,
          error: `Server responded with ${response.status}: ${response.statusText}`,
          statusCode: response.status
        };
      }
      
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      return this.handleError(error);
    }
  }
  
  /**
   * Get map state
   */
  public async getMapState(): Promise<ApiResponse<any>> {
    try {
      const headers = await this.getHeaders();
      
      const response = await fetch(this.baseUrl, {
        method: 'GET',
        headers
      });
      
      if (!response.ok) {
        return {
          success: false,
          error: `Server responded with ${response.status}: ${response.statusText}`,
          statusCode: response.status
        };
      }
      
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      return this.handleError(error);
    }
  }
}

/**
 * API service for preferences
 */
class PreferencesApiService extends BaseApiService {
  constructor() {
    super('preferences');
  }
  
  /**
   * Save preferences
   */
  public async savePreferences(preferences: any): Promise<ApiResponse<any>> {
    try {
      const headers = await this.getHeaders();
      
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(preferences)
      });
      
      if (!response.ok) {
        return {
          success: false,
          error: `Server responded with ${response.status}: ${response.statusText}`,
          statusCode: response.status
        };
      }
      
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Add to PreferencesApiService class
  public async savePreferencesDelta(preferences: any): Promise<ApiResponse<any>> {
    try {
      const headers = await this.getHeaders();
      const response = await fetch(`${this.baseUrl}/delta`, {
        method: 'POST',
        headers,
        body: JSON.stringify(preferences)
      });
      
      if (!response.ok) {
        return {
          success: false,
          error: `Server responded with ${response.status}: ${response.statusText}`,
          statusCode: response.status
        };
      }
      
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      return this.handleError(error);
    }
  }
  
  /**
   * Get preferences
   */
  public async getPreferences(): Promise<ApiResponse<any>> {
    try {
      const headers = await this.getHeaders();
      
      const response = await fetch(this.baseUrl, {
        method: 'GET',
        headers
      });
      
      if (!response.ok) {
        return {
          success: false,
          error: `Server responded with ${response.status}: ${response.statusText}`,
          statusCode: response.status
        };
      }
      
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      return this.handleError(error);
    }
  }
}

// Create and export singleton instances
export const conversationApiService = new ConversationApiService();
export const locationApiService = new LocationApiService();
export const weatherApiService = new WeatherApiService();
export const imageApiService = new ImageApiService();
export const mapStateApiService = new MapStateApiService();
export const preferencesApiService = new PreferencesApiService();
